import axios from "axios";
import { jwtDecode } from 'jwt-decode'

//this is example of auth header
import authHeader from './authheader.js'

const getIndividualDashboard = () => {
    return axios.get(process.env.REACT_APP_APIURL + "/api/Individual/dashboard", { headers: authHeader() });
}
//end example

const REGISTERAPIURL = process.env.REACT_APP_APIURL + "/api/Register";
const API_AUTH_URL = process.env.REACT_APP_APIURL + "/api/Auth";

const registerIndividual = (data) => {
    return axios.post(`${REGISTERAPIURL}/individual`, data);

};
const forgotPassword = (username) => {
    return axios.post(`${API_AUTH_URL}/forgotpassword`, null, { params: { username } })
        .then((response) => {
            return response;
        });
}
const forgotIndividualUserId = (email, dateOfBirth) => {
    return axios.post(`${API_AUTH_URL}/forgotindividualuserid`, null, { params: { email, dateOfBirth } })
        .then((response) => {
            return response;
        });
};
const forgotFirmUserId = (email, credential) => {
    return axios.post(`${API_AUTH_URL}/forgotfirmuserid`, null, { params: { email, credential } })
        .then((response) => {
            return response;
        });
};
const registerFirm = (data) => {
    return axios.post(`${REGISTERAPIURL}/firm`, data)
};
const NAMEIDENTIFIER_CLAIM = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier";
const NAME_CLAIM = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name";
const ROLE_CLAIM = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";

const rolesFromDecoded = (decoded) => {
    const roles = decoded[ROLE_CLAIM];
    if (!roles) {
        return [];
    }
    return Array.isArray(roles) ? roles : [roles];
};

const login = (username, password) => {
    return axios.post(`${API_AUTH_URL}/login`, { username, password })
        .then((response) => {
            if (response.data.message) {
                localStorage.setItem("user", JSON.stringify(response.data));
                upsertCachedAccount(response.data);
            }
            return response.data;
        });
};

const upsertCachedAccount = (accountBlob) => {
    const decoded = jwtDecode(accountBlob.message);
    const id = decoded[NAMEIDENTIFIER_CLAIM];
    const accounts = readCachedAccounts().filter((a) => {
        try {
            return jwtDecode(a.message)[NAMEIDENTIFIER_CLAIM] !== id;
        } catch {
            return false;
        }
    });
    accounts.push(accountBlob);
    localStorage.setItem("cachedAccounts", JSON.stringify(accounts));
};

const readCachedAccounts = () => {
    try {
        return JSON.parse(localStorage.getItem("cachedAccounts")) || [];
    } catch {
        return [];
    }
};

const resendEmailVerification = (username) => {
    return axios.post(`${API_AUTH_URL}/resendemailverification`, null, { params: { username } })
        .then((response) => {
            return response;
        });
};

const logout = () => {
    clearAccountScopedStorage();
    localStorage.removeItem("user");
    localStorage.removeItem("persist:user");
    localStorage.removeItem("cachedAccounts");
};

const getCurrentUser = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    //check if expired
    //if so, call logout and return null
    if (user) {
        const expire = new Date(user.expireDate);
        if (expire < new Date()) {
            logout();
            return null;
        }
    }
    return user;
};

const getRoles = () => {
    var user = getCurrentUser();
    if (user == null) {
        return [];
    }

    return rolesFromDecoded(jwtDecode(user.message));
}

const getCachedAccounts = () => {
    const currentUser = getCurrentUser();
    const currentId = currentUser ? jwtDecode(currentUser.message)[NAMEIDENTIFIER_CLAIM] : null;
    const now = new Date();
    const stillValid = [];
    const result = [];
    readCachedAccounts().forEach((account) => {
        if (new Date(account.expireDate) < now) {
            return; // prune expired entries
        }
        stillValid.push(account);
        const decoded = jwtDecode(account.message);
        result.push({
            nameIdentifier: decoded[NAMEIDENTIFIER_CLAIM],
            formattedName: decoded.FormattedName,
            lolaNumber: decoded.LolaNumber,
            userName: decoded[NAME_CLAIM],
            roles: rolesFromDecoded(decoded),
            isCurrent: decoded[NAMEIDENTIFIER_CLAIM] === currentId,
            account
        });
    });
    localStorage.setItem("cachedAccounts", JSON.stringify(stillValid));
    return result;
};

// Mirrors what redux-persist writes to localStorage["persist:user"] when LOGIN_SUCCEEDED
// is dispatched on a normal login (see App.js's userConfig + CustomReducer.js's userInfo
// reducer). Switching bypasses that dispatch (it's a hard reload, not a React action), so
// without this the persisted userInfo slice - and anything reading it, e.g. the top-nav
// FormattedName - would keep showing the previous account after a switch.
const writePersistedUserInfo = (decodedClaims) => {
    const storageState = {};
    Object.keys(decodedClaims).forEach((key) => {
        storageState[key] = JSON.stringify(decodedClaims[key]);
    });
    storageState._persist = JSON.stringify({ version: -1, rehydrated: true });
    localStorage.setItem("persist:user", JSON.stringify(storageState));
};

// Everything scoped to "whoever is currently logged in" that isn't part of
// the cachedAccounts/user/persist:user bookkeeping above. Mirrors (and
// extends) what Main.js's sendToLogin() clears on a normal logout - plus the
// PERSIST_CURRENT_APP_* keys it was missing, which track the in-progress
// application a Staff/Board user is reviewing (Internal Applications/
// Renewals/Reinstatements Index pages). Without clearing those, switching
// from one Staff account to another could carry the first account's
// in-progress application id into the second account's session.
const clearAccountScopedStorage = () => {
    sessionStorage.removeItem("persist:root");
    sessionStorage.removeItem("currentSideNavKey");
    localStorage.removeItem("updateRecentQueue");
    localStorage.removeItem("updateRecentContacts");
    localStorage.removeItem("currentPageCache");
    localStorage.removeItem("searchCache");
    localStorage.removeItem("displayLengthCache");
    localStorage.removeItem("PERSIST_CURRENT_ROUTE");
    localStorage.removeItem("PERSIST_CURRENT_APP_ID");
    localStorage.removeItem("PERSIST_CURRENT_APP_ENTITY_ID");
    localStorage.removeItem("PERSIST_CURRENT_APP_TYPE_ID");
};

// Updates which cached account is "active" without navigating anywhere -
// safe to call when the caller is staying on the current page (e.g. the
// switch-accounts list re-reading its own state right after).
const setActiveAccount = (accountBlob) => {
    localStorage.setItem("user", JSON.stringify(accountBlob));
    writePersistedUserInfo(jwtDecode(accountBlob.message));
    clearAccountScopedStorage();
};

const switchToAccount = (accountBlob, redirectTo = "/") => {
    setActiveAccount(accountBlob);
    window.location.href = redirectTo;
};

const removeCachedAccount = (nameIdentifier) => {
    const currentUser = getCurrentUser();
    const currentId = currentUser ? jwtDecode(currentUser.message)[NAMEIDENTIFIER_CLAIM] : null;
    const remaining = readCachedAccounts().filter((a) => jwtDecode(a.message)[NAMEIDENTIFIER_CLAIM] !== nameIdentifier);
    localStorage.setItem("cachedAccounts", JSON.stringify(remaining));

    if (nameIdentifier !== currentId) {
        return; // removed a non-active cached account, nothing else to do
    }

    if (remaining.length > 0) {
        setActiveAccount(remaining[0]); // stay on this page, no navigation needed
    } else {
        logout();
        window.location.href = "/login";
    }
};

export default {
    registerIndividual,
    registerFirm,
    login,
    logout,
    getCurrentUser,
    getRoles,
    forgotPassword,
    resendEmailVerification,
    forgotIndividualUserId,
    forgotFirmUserId,
    getCachedAccounts,
    switchToAccount,
    removeCachedAccount
}